import React, { useContext } from 'react';
import styled from 'styled-components';
import Card from '@material-ui/core/Card';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { CardContent, TextField, Typography } from '@material-ui/core';
import { FilterList } from '@material-ui/icons';
import { Box, Flex } from '@rebass/grid';
import { useIsMobile } from '../../../lib/hooks/use-is-mobile';
import { Button } from '@mui/material';
import { useUser } from '../../../lib/hooks/use-user';
import { AppContext } from '../../../app';
import { useGymPrograms } from '../../../lib/hooks/use-gym-program';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { colors } from '../../../config/theme';

function RadioButtonsGroup({ setLastPromoted, lastPromoted }) {
  // Controlled: the mobile drawer mounts its own FilterPanel, so an
  // uncontrolled group would show "Last Promotion" there no matter what the
  // current filter actually is.
  return (
    <RadioGroup
      aria-labelledby="demo-radio-buttons-group-label"
      value={lastPromoted ? 'lastPromoted' : 'date'}
      onChange={(e) => setLastPromoted(e.target.value === 'lastPromoted')}
      name="radio-buttons-group"
    >
      <Flex justifyContent="flex-end">
        <FormControlLabel
          value="lastPromoted"
          control={<Radio sx={{ color: colors.textPrimary }} />}
          label="Last Promotion"
        />
        <FormControlLabel
          value="date"
          control={<Radio sx={{ color: colors.textPrimary }} />}
          label="Date Range"
        />
      </Flex>
    </RadioGroup>
  );
}

const FilterWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: 'search filter';
  grid-gap: 16px;

  .MuiFormControl-root {
    width: 100%;
  }

  @media (max-width: 910px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'search'
      'filter';
  }

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    grid-template-areas: 'search filter-icon';
  }
`;

const FilterControls = styled.div`
  display: grid;
  grid-template-areas:
    'age s-date e-date'
    '... radio radio';
  grid-gap: 16px;
  @media (max-width: 651px) {
    grid-template-areas:
      'age age'
      's-date e-date'
      'radio radio';
  }
`;

const GridArea = styled.div`
  grid-area: ${(props) => props.area};
`;

export const FilterPanel = ({
  program,
  handleProgramChange,
  startDate,
  handleStartDateChange,
  endDate,
  handleEndDateChange,
  lastPromoted,
  setLastPromoted,
}) => {
  const { loggedInUserId } = useContext(AppContext);
  const { user: currentUser } = useUser(loggedInUserId);
  const { gymPrograms = [] } = useGymPrograms(currentUser?.gym?.id);
  return (
    <FilterControls>
      <GridArea area="age">
        <FormControl sx={{ color: colors.textPrimary, minWidth: 120, width: '100%' }}>
          {/*
            The label was rendering on top of the selected program. It only
            shrinks out of the way when MUI marks the control filled, so it is
            pinned shrunk here instead; displayEmpty both renders "All" in the
            box when nothing is selected and makes MUI notch the outline, so
            the label always has a gap to sit in.
          */}
          <InputLabel id="program-filter-label" shrink sx={{ color: colors.textPrimary }}>
            Filter by Program
          </InputLabel>
          <Select
            labelId="program-filter-label"
            id="program-filter"
            value={program || ''}
            label="Filter by Program"
            onChange={handleProgramChange}
            variant="outlined"
            displayEmpty
            sx={{ color: colors.textPrimary }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {gymPrograms.map((gymProgram) => (
              <MenuItem key={gymProgram?.id} value={gymProgram?.id}>
                {gymProgram?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </GridArea>
      <GridArea area="s-date">
        <TextField
          type="date"
          value={startDate}
          onChange={(e) => handleStartDateChange(e)}
          variant="outlined"
          label="Start Date"
          style={{ width: '100%' }}
          disabled={lastPromoted}
        />
      </GridArea>
      <GridArea area="e-date">
        <TextField
          type="date"
          value={endDate}
          variant="outlined"
          onChange={(e) => handleEndDateChange(e)}
          label="End Date"
          style={{ width: '100%' }}
          disabled={lastPromoted}
        />
      </GridArea>
      <GridArea area="radio">
        <RadioButtonsGroup
          setLastPromoted={setLastPromoted}
          lastPromoted={lastPromoted}
        />
      </GridArea>
    </FilterControls>
  );
};

export const UserSearchFilters = ({
  handleProgramChange,
  program,
  startDate,
  handleStartDateChange,
  endDate,
  handleEndDateChange,
  setLastPromoted,
  lastPromoted,
  setSearchTerm,
  toggleFilterDrawer,
  backTo,
}) => {
  const { isMobile } = useIsMobile();
  const { loggedInUserId } = useContext(AppContext);
  const { user: currentUser } = useUser(loggedInUserId);
  const { gymPrograms = [] } = useGymPrograms(currentUser?.gym?.id);
  // On mobile the panel is hidden behind the drawer, so the button has to say
  // what is currently filtered — otherwise a default program looks like no
  // filter at all.
  const activeProgram = gymPrograms.find(
    (gymProgram) => gymProgram?.id === program
  );

  return (
    <Box mt="22px" mb="22px">
      {backTo && (
        <Flex mb="16px" justifyContent="space-between">
          <Typography variant="h6">Add Invitees</Typography>
          <Button onClick={backTo} variant="outlined" color="primary">
            Save changes
          </Button>
        </Flex>
      )}
      <Card>
        <CardContent>
          <Flex justifyContent="space-between" mb="16px">
            <Typography variant="h6">Filter Members</Typography>
          </Flex>
          <FilterWrapper>
            <GridArea area="search">
              <Box>
                <TextField
                  sx={{ width: '100%' }}
                  label="Search by name"
                  variant="outlined"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Box>
            </GridArea>

            {!isMobile && (
              <FilterPanel
                program={program}
                handleProgramChange={handleProgramChange}
                startDate={startDate}
                handleStartDateChange={handleStartDateChange}
                endDate={endDate}
                handleEndDateChange={handleEndDateChange}
                lastPromoted={lastPromoted}
                setLastPromoted={setLastPromoted}
              />
            )}
            {isMobile && (
              <GridArea area="filter-icon">
                <Button
                  sx={{
                    color: colors.textPrimary,
                    borderColor: colors.borderMuted,
                    height: '56px',
                  }}
                  onClick={toggleFilterDrawer}
                  variant="outlined"
                >
                  <FilterList />
                  <Box ml="8px">{activeProgram?.name || 'All'}</Box>
                </Button>
              </GridArea>
            )}
          </FilterWrapper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserSearchFilters;
